/**
 * Search bar — non-modal dialog controller.
 *
 * Opens the search modal as a non-modal dialog (dialog.show(), no focus trap)
 * when the header search bar is focused or clicked.
 *
 * This bypasses dialog-component.showDialog() deliberately to avoid the
 * browser focus trap that showModal() creates.
 *
 */

const searchModalComponent = document.getElementById("search-modal");

const dialogEl = /** @type {HTMLDialogElement | null} */ (
  searchModalComponent?.querySelector("dialog")
);

const modalSearchInput = /** @type {HTMLInputElement | null} */ (
  dialogEl?.querySelector('[ref="searchInput"]')
);

// Module-level state — shared because there is only one dialog
let isClosing = false;
let previousScrollY = 0;
let activeClickHandler = /** @type {((e: Event) => void) | null} */ (null);
let activeKeyDownHandler = /** @type {((e: KeyboardEvent) => void) | null} */ (
  null
);
let activeBarInput = /** @type {HTMLInputElement | null} */ (null);

// Override dialog.close so isClosing is set synchronously BEFORE the browser
// restores focus to barInput. The native "close" event fires asynchronously
// (queued as a task), after focus is already restored — too late to guard.
if (dialogEl) {
  const nativeClose = dialogEl.close.bind(dialogEl);
  /** @param {...any} args */
  dialogEl.close = function (...args) {
    isClosing = true;
    nativeClose(...args);
    // Our closeModal also resets isClosing; this rAF covers external closes.
    requestAnimationFrame(() => {
      isClosing = false;
    });
  };
}

// Restore scroll lock and clean up listeners when the dialog is closed
// externally (e.g. dialog-component's mobile close button). closeModal handles
// this itself — the null checks and guards prevent double-running.
dialogEl?.addEventListener("close", () => {
  if (activeClickHandler) {
    document.removeEventListener("click", activeClickHandler, {
      capture: true,
    });
    activeClickHandler = null;
  }
  if (activeKeyDownHandler) {
    document.removeEventListener("keydown", activeKeyDownHandler);
    activeKeyDownHandler = null;
  }
  activeBarInput?.blur();
  activeBarInput = null;
  modalSearchInput?.blur();
  document.documentElement.removeAttribute("scroll-lock");
  if (document.body.style.position === "fixed") {
    document.body.style.width = "";
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo({ top: previousScrollY, behavior: "instant" });
  } else {
    document.documentElement.style.overflow = "";
  }
});

/**
 * Opens the search modal using dialog.show() so focus is not trapped.
 *
 * Background scrolling is locked for as long as the modal is open, via the
 * theme's shared `[scroll-lock]` attribute (see base.css, services-drawer.js).
 *
 * @param {HTMLInputElement} barInput - The input inside the triggering <search-bar>
 * @param {(e: Event) => void} handleDocumentClick
 * @param {(e: KeyboardEvent) => void} handleDocumentKeyDown
 */
function openModal(barInput, handleDocumentClick, handleDocumentKeyDown) {
  if (dialogEl?.open || isClosing) return;

  previousScrollY = window.scrollY;
  activeBarInput = barInput;

  if (window.innerWidth >= 750) {
    const headerGroup = document.querySelector(".header-section, header");
    if (headerGroup && dialogEl) {
      // .search-modal__content is position: absolute (document-relative), so
      // the viewport-relative rect from getBoundingClientRect() must be
      // converted to a document-relative offset by adding the current scroll
      // position. Without this, opening the modal while scrolled placed it
      // near the very top of the page — off-screen above the viewport —
      // which made it look like the popup opened and immediately closed.
      dialogEl.style.top = `${window.scrollY + headerGroup.getBoundingClientRect().bottom}px`;
    }
  }

  dialogEl?.show();
  document.documentElement.setAttribute("scroll-lock", "");

  barInput.focus({ preventScroll: true });

  searchModalComponent?.dispatchEvent(new CustomEvent("dialog:open"));

  // Defer click listener by one tick so the tap/click that triggered openModal
  // has already fired before we start watching for outside clicks. Without this,
  // the same event that opens the modal immediately closes it on mobile.
  setTimeout(() => {
    activeClickHandler = handleDocumentClick;
    activeKeyDownHandler = handleDocumentKeyDown;
    document.addEventListener("click", handleDocumentClick, { capture: true });
    document.addEventListener("keydown", handleDocumentKeyDown);
  }, 0);
}

/**
 * Closes the search modal, running the slide-out animation first,
 * then restoring body scroll if it was locked by another surface.
 *
 * @param {(e: Event) => void} handleDocumentClick
 * @param {(e: KeyboardEvent) => void} handleDocumentKeyDown
 */
async function closeModal(handleDocumentClick, handleDocumentKeyDown) {
  if (!dialogEl?.open || isClosing) return;

  isClosing = true;

  document.removeEventListener("click", handleDocumentClick, { capture: true });
  document.removeEventListener("keydown", handleDocumentKeyDown);
  activeClickHandler = null;
  activeKeyDownHandler = null;

  // Drop the focused/active look from the search input(s) once the modal closes.
  activeBarInput?.blur();
  activeBarInput = null;
  modalSearchInput?.blur();

  document.documentElement.removeAttribute("scroll-lock");

  // Trigger closing animation — mirrors the pattern in dialog.js
  dialogEl.style.animation = "none";
  void dialogEl.offsetWidth; // Force reflow to restart the animation
  dialogEl.classList.add("dialog-closing");
  dialogEl.style.animation = "";

  // Wait for all CSS animations to complete (DocumentTimeline only)
  const animations = dialogEl
    .getAnimations({ subtree: false })
    .filter((a) => a.timeline instanceof DocumentTimeline)
    .map((a) => a.finished);

  await Promise.allSettled(
    animations.length ? animations : [Promise.resolve()],
  );

  // Restore scroll lock before dialog.close() so focus restoration doesn't
  // fire while the page is still locked — matches dialog.js order.
  if (document.body.style.position === "fixed") {
    document.body.style.width = "";
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo({ top: previousScrollY, behavior: "instant" });
  } else {
    document.documentElement.style.overflow = "";
  }

  dialogEl.close();
  dialogEl.classList.remove("dialog-closing");

  isClosing = false;

  searchModalComponent?.dispatchEvent(new CustomEvent("dialog:close"));
}

// Lets other scripts (e.g. br-autosuggest-recommendations.liquid, when it
// detects its results state has desynced from Bloomreach's widget) close
// this modal through the normal animated closeModal() path, without needing
// access to its module-private handlers directly.
document.addEventListener("br:close-search-modal", () => {
  if (activeClickHandler && activeKeyDownHandler) {
    closeModal(activeClickHandler, activeKeyDownHandler);
  }
});

/** @param {number} ms */
function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

class SearchBar extends HTMLElement {
  /** @type {boolean} */
  #dialogOpen = false;

  /** @type {boolean} */
  #animationCancelled = false;

  /** @type {{ prefix: string, variants: string[] } | null} */
  #placeholderConfig = null;

  /** @type {HTMLSpanElement | null} */
  #placeholderSpan = null;

  /** @type {HTMLInputElement | null} */
  #barInput = null;

  disconnectedCallback() {
    this.#animationCancelled = true;
    if (searchModalComponent) {
      searchModalComponent.removeEventListener(
        "dialog:open",
        this.#onDialogOpen,
      );
      searchModalComponent.removeEventListener(
        "dialog:close",
        this.#onDialogClose,
      );
    }
  }

  #onDialogOpen = () => {
    this.#dialogOpen = true;
    if (this.#barInput) this.#syncFauxPlaceholder(this.#barInput);
  };

  #onDialogClose = () => {
    this.#dialogOpen = false;
    if (this.#barInput) this.#syncFauxPlaceholder(this.#barInput);
  };

  /**
   * @param {HTMLInputElement} barInput
   */
  #syncFauxPlaceholder(barInput) {
    const hide =
      this.#dialogOpen ||
      Boolean(barInput.value?.trim()) ||
      document.activeElement === barInput;

    this.classList.toggle("search-bar--faux-placeholder-hidden", hide);

    if (!hide) {
      barInput.placeholder = "";
    }
  }

  /**
   * @param {HTMLInputElement} barInput
   */
  async #runPlaceholderLoop(barInput) {
    const span = this.#placeholderSpan;
    const config = this.#placeholderConfig;

    if (!span || !config?.prefix || !config.variants?.length) return;

    const { prefix, variants } = config;

    while (!this.#animationCancelled) {
      for (const variant of variants) {
        if (this.#animationCancelled) return;

        span.textContent = prefix;
        await delay(280);
        if (this.#animationCancelled) return;

        for (let d = 1; d <= 3; d++) {
          span.textContent = `${prefix}${".".repeat(d)}`;
          await delay(220);
          if (this.#animationCancelled) return;
        }

        const target = `${span.textContent} ${variant}`;
        for (let i = span.textContent.length; i < target.length; i++) {
          if (this.#animationCancelled) return;
          span.textContent = target.slice(0, i + 1);
          await delay(42);
        }

        await delay(2600);
        if (this.#animationCancelled) return;
      }
    }
  }

  /** @param {HTMLInputElement} barInput */
  #syncClearButton(barInput) {
    this.classList.toggle("search-bar--has-value", Boolean(barInput.value));
  }

  connectedCallback() {
    const barInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(".search-bar__input")
    );

    if (!barInput) return;

    this.#barInput = barInput;
    this.#placeholderSpan = this.querySelector(".search-bar__typed-placeholder");

    // Suppress the native "please fill out this field" bubble on empty
    // submit — invalid submission is still blocked, just silently.
    barInput.addEventListener("invalid", (event) => {
      event.preventDefault();
    });

    const clearButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(".search-bar__clear")
    );

    if (clearButton) {
      clearButton.addEventListener("click", () => {
        barInput.value = "";
        this.#syncClearButton(barInput);
        barInput.focus();
        barInput.dispatchEvent(new InputEvent("input", { bubbles: true, inputType: "deleteContentBackward" }));
      });
    }

    const configEl = this.querySelector(".search-bar__placeholder-config");
    if (configEl?.textContent) {
      try {
        this.#placeholderConfig = JSON.parse(configEl.textContent.trim());
      } catch {
        this.#placeholderConfig = null;
      }
    }

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const hasAnimatedPlaceholder =
      !reducedMotion &&
      Boolean(this.#placeholderConfig?.prefix) &&
      Array.isArray(this.#placeholderConfig?.variants) &&
      this.#placeholderConfig.variants.length > 0;

    const sync = () => this.#syncFauxPlaceholder(barInput);
    const syncClear = () => this.#syncClearButton(barInput);

    barInput.addEventListener("input", syncClear);

    if (hasAnimatedPlaceholder) {
      barInput.placeholder = "";
      this.#placeholderSpan?.classList.remove("visually-hidden");

      searchModalComponent?.addEventListener("dialog:open", this.#onDialogOpen);
      searchModalComponent?.addEventListener("dialog:close", this.#onDialogClose);

      barInput.addEventListener("focus", sync);
      barInput.addEventListener("blur", sync);
      barInput.addEventListener("input", sync);

      void this.#runPlaceholderLoop(barInput);
    } else {
      if (reducedMotion && this.dataset.staticPlaceholder) {
        barInput.placeholder = this.dataset.staticPlaceholder;
      }
      this.#placeholderSpan?.classList.add("visually-hidden");
    }

    if (!searchModalComponent || !dialogEl) return;

    /**
     * Closes the modal when clicking outside the dialog and its trigger.
     * @param {Event} event
     */
    const handleDocumentClick = (event) => {
      if (!dialogEl?.open) return;

      const target = /** @type {Element} */ (event.target);

      // Keep open when clicking inside the dialog
      if (dialogEl.contains(target)) return;

      // Keep open when clicking any <search-bar> trigger (outside <dialog> in DOM)
      if (target.closest("search-bar")) return;

      closeModal(handleDocumentClick, handleDocumentKeyDown);
    };

    /**
     * Closes the modal when the Escape key is pressed.
     * @param {KeyboardEvent} event
     */
    const handleDocumentKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeModal(handleDocumentClick, handleDocumentKeyDown);
    };

    // When BR autosuggest is enabled, the recs panel (br-autosuggest-recommendations.liquid)
    // relocates Bloomreach's own dropdown into itself and manages its own
    // states (top sellers / results / no results), so the modal just stays
    // open for as long as the user is engaged with the input — no need to
    // close it once BR's dropdown appears.
    if (this.dataset.bloomreachAutosuggest === "true") {
      barInput.addEventListener("focus", () => {
        openModal(barInput, handleDocumentClick, handleDocumentKeyDown);
      });
    }

    // Mirror this input's value → modal's internal search input so
    // predictive-search.js fires its debounced search as the user types.
    // Must use InputEvent (not Event) because predictive-search.js guards on
    // event.inputType, which is only present on InputEvent.
    if (modalSearchInput) {
      barInput.addEventListener("input", (event) => {
        modalSearchInput.value = barInput.value;
        modalSearchInput.dispatchEvent(
          new InputEvent("input", {
            bubbles: true,
            inputType:
              /** @type {InputEvent} */ (event).inputType || "insertText",
          }),
        );
      });
    }

    if (hasAnimatedPlaceholder) {
      sync();
    }
  }
}

customElements.define("search-bar", SearchBar);
